import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, SpinnerLoader, type IDropdownItem } from 'shared/ui'
import { SelectFieldMultiList } from 'shared/ui/Select/SelectMultiListField/SelectMultiListField'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { useElementWidth } from 'shared/hooks/useElementWidth'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar'
import {
  type ConversationAssignmentStore,
  defaultUnassignedDropdownItem,
  defaultRoundRobinDropdownItem,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/store/conversationAssignmentStore'
import styles from './styles.module.scss'

export const CRMConversationAssignment = observer(
  ({ store }: { store: ConversationAssignmentStore }) => {
    const {
      tabSwitchingLoading,
      membersLoading,
      filteredCRMFallbackMembers,
      selectedCRMFallbackMember,
      setSelectedCRMFallbackMember,
      crmHubspotProperty,
      selectedHubspotFieldItem,
      hubspotFieldsAsDropdownItems,
      loadHubspotAssignmentFields,
      setCRMHubspotProperty,
      crmFallbackType,
      setCRMFallbackType,
      crmFallbackRoundRobinMembers,
      setCRMFallbackRoundRobinMember,
      deleteCRMFallbackRoundRobinMember,
      updateCRMFallbackRoundRobinUsers,
      updateManualAssignment,
      filteredRoundRobinMembers,
    } = store
    const hubspotPropertyRef = useRef<HTMLDivElement>(null)
    const fallbackRef = useRef<HTMLDivElement>(null)
    const hubspotPropertyWidth = useElementWidth(hubspotPropertyRef)
    const fallbackWidth = useElementWidth(fallbackRef)

    useEffect(() => {
      if (hubspotFieldsAsDropdownItems.length === 0) {
        loadHubspotAssignmentFields()
      }
    }, [])

    const handleFallbackChange = (value: string | number, item: IDropdownItem) => {
      if (value === 'unassigned') {
        setCRMFallbackType('unassigned')
        setSelectedCRMFallbackMember(defaultUnassignedDropdownItem)
      } else if (value === 'round_robin') {
        setCRMFallbackType('round_robin')
        setSelectedCRMFallbackMember(defaultRoundRobinDropdownItem)
      } else {
        const userId = Number(item.id)
        setCRMFallbackType(null)
        setSelectedCRMFallbackMember(item)
        updateManualAssignment(userId, { keepHubspotEnabled: true })
      }
    }

    return (
      <>
        {tabSwitchingLoading || membersLoading ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
            <SpinnerLoader ariaLabel={'CRMConversationAssignment'} />
          </Box>
        ) : null}
        <Box
          display='flex'
          gap='10px'
          width='100%'
          justifyContent='space-between'
          style={{ visibility: tabSwitchingLoading || membersLoading ? 'hidden' : 'visible' }}
        >
          <Box display='flex' flexDirection='column' gap='4px' width='100%'>
            <div ref={hubspotPropertyRef} style={{ width: '100%' }}>
              <SelectField
                labelProps={{
                  label: {
                    text: 'HubSpot user property',
                  },
                }}
                withSearch={false}
                items={hubspotFieldsAsDropdownItems}
                item={selectedHubspotFieldItem}
                value={crmHubspotProperty}
                setValue={(value) => setCRMHubspotProperty(value)}
                placeholder={!crmHubspotProperty ? 'Deleted Property' : 'Contact owner'}
                size='medium'
                dropdownProps={{
                  width: hubspotPropertyWidth,
                  PopperProps: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          offset: ({ reference }) => {
                            return [0, -reference.height - 4]
                          },
                        },
                      },
                    ],
                  },
                }}
              />
            </div>
          </Box>
          <Box display='flex' flexDirection='column' gap='4px' width='100%'>
            <div ref={fallbackRef} style={{ width: '100%' }}>
              <SelectField
                labelProps={{
                  label: {
                    text: 'Fallback',
                  },
                }}
                items={filteredCRMFallbackMembers}
                item={selectedCRMFallbackMember}
                value={selectedCRMFallbackMember?.value || undefined}
                setValue={handleFallbackChange}
                placeholder='Select'
                size='medium'
                btnProps={{
                  text: selectedCRMFallbackMember ? (
                    <ChipWithAvatar
                      item={selectedCRMFallbackMember}
                      label={selectedCRMFallbackMember.label}
                      iconType='div'
                      chipWithoutBorder
                    />
                  ) : undefined,
                }}
                dropdownProps={{
                  width: fallbackWidth,
                  PopperProps: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          offset: ({ reference }) => {
                            return [0, -reference.height - 4]
                          },
                        },
                      },
                    ],
                  },
                }}
              />
            </div>
          </Box>
        </Box>
        {crmFallbackType === 'round_robin' && (
          <Box display='grid' gap='4px' marginTop='24px'>
            <SelectFieldMultiList
              withInitialSearch
              ChipComponent={ChipWithAvatar}
              labelText='Select the inbox members you want to include in round robin assignment'
              maxVisibleChips={5}
              placeholderClassName={styles.multiSelectPlaceholder}
              dropdownProps={{
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        offset: ({ reference }) => {
                          return [0, -reference.height - 8]
                        },
                      },
                    },
                  ],
                },
                onClose: updateCRMFallbackRoundRobinUsers,
              }}
              items={filteredRoundRobinMembers}
              selectedItems={crmFallbackRoundRobinMembers}
              onDelete={deleteCRMFallbackRoundRobinMember}
              onSelect={setCRMFallbackRoundRobinMember}
              fullWidth
              customComponent={() => (
                <MultiSelectDropdownContent
                  ChipComponent={ChipWithAvatar}
                  items={filteredRoundRobinMembers}
                  selectedItems={crmFallbackRoundRobinMembers}
                  onSelect={setCRMFallbackRoundRobinMember}
                  onDelete={deleteCRMFallbackRoundRobinMember}
                  notFoundText='No more members'
                  shouldShowNotFoundText
                />
              )}
            />
          </Box>
        )}
      </>
    )
  }
)
